import type {
  WebsiteBlockDataSource,
  WebsiteBlockManifest,
  WebsiteComponentId,
  WebsiteLayoutFamily,
  WebsitePageKind,
  WebsiteSlotDefault,
  WebsiteSlotSchema,
  WebsiteTemplateManifest,
  WebsiteThemeSchema,
} from '../../types/website-templates';
import {
  WEBSITE_COLOR_TOKENS,
  WEBSITE_EMPTY_BEHAVIORS,
  WEBSITE_FONT_IDS,
  WEBSITE_LAYOUT_FAMILIES,
  WEBSITE_LINK_TARGET_KINDS,
  WEBSITE_MEDIA_ASPECTS,
  WEBSITE_MEDIA_KINDS,
  WEBSITE_MERCHANDISING_KEYS,
  WEBSITE_PAGE_KINDS,
  WEBSITE_SLOT_SEMANTICS,
  WEBSITE_SLOT_TYPES,
} from '../../types/website-templates';
import {
  WEBSITE_COMPONENT_SURFACE,
  WEBSITE_REQUIRED_CHROME_COMPONENTS,
  WEBSITE_REQUIRED_PAGE_COMPONENT,
  WEBSITE_RENDERER_MAJOR,
  isSupportedWebsiteVariant,
} from './capabilities';
import { isWebsiteHexColor } from './contrast';
import {
  WEBSITE_BLOCK_ID_RE,
  WEBSITE_DOCUMENT_MAX_BYTES,
  WEBSITE_MAX_SLOTS,
  WEBSITE_SLOT_ID_MAX_LENGTH,
  WEBSITE_SLOT_ID_RE,
  WEBSITE_TEMPLATE_ASSET_KEY_MAX_LENGTH,
  WEBSITE_TEMPLATE_ASSET_KEY_RE,
  WEBSITE_VARIANT_RE,
  isOneOf,
  isRecord,
  utf8ByteLength,
  type UnknownRecord,
} from './internal';
import { websiteSlotItemPath } from './edit-regions';
import { WEBSITE_CHROME_SLOT_SCOPE, slotScopeForPageKind } from './page-kinds';
import { validateWebsiteLinkValue } from './slots';
import { WEBSITE_TEXT_MAX_LENGTH, checkMerchantText } from './text-safety';
import { findContrastFailures } from './theme';

export { WEBSITE_COMPONENT_VARIANTS } from './capabilities';

export const WEBSITE_MAX_BLOCKS_PER_PAGE = 24;
export const WEBSITE_MAX_CHROME_BLOCKS = 4;
export const WEBSITE_MAX_SLOT_ORDINAL = 12;
export const WEBSITE_LEGACY_SLOT_SCOPE = 'legacy';

export type WebsiteManifestParse =
  | { readonly ok: true; readonly manifest: WebsiteTemplateManifest }
  | { readonly ok: false; readonly issues: readonly string[] };

function hasExactKeys(
  value: UnknownRecord,
  required: readonly string[],
  optional: readonly string[] = [],
): boolean {
  const keys = Object.keys(value);
  return (
    required.every((key) => keys.includes(key)) &&
    keys.every((key) => required.includes(key) || optional.includes(key))
  );
}

function parseDataSource(value: unknown): WebsiteBlockDataSource | null {
  if (!isRecord(value)) return null;
  if (value.kind === 'merchandising') {
    if (!hasExactKeys(value, ['kind', 'key', 'limit'])) return null;
    if (!isOneOf(WEBSITE_MERCHANDISING_KEYS, value.key)) return null;
    if (!Number.isInteger(value.limit) || (value.limit as number) < 1) return null;
    if ((value.limit as number) > 24) return null;
    return { kind: 'merchandising', key: value.key, limit: value.limit as number };
  }
  if (value.kind === 'public-categories') {
    if (!hasExactKeys(value, ['kind', 'limit'])) return null;
    if (!Number.isInteger(value.limit) || (value.limit as number) < 1) return null;
    if ((value.limit as number) > 12) return null;
    return { kind: 'public-categories', limit: value.limit as number };
  }
  if (value.kind === 'category-products') {
    if (!hasExactKeys(value, ['kind', 'categoryOrdinal', 'limit'])) return null;
    if (![1, 2, 3].includes(value.categoryOrdinal as number)) return null;
    if (
      !Number.isInteger(value.limit) ||
      (value.limit as number) < 1 ||
      (value.limit as number) > 8
    )
      return null;
    return {
      kind: 'category-products',
      categoryOrdinal: value.categoryOrdinal as 1 | 2 | 3,
      limit: value.limit as number,
    };
  }
  if (value.kind === 'bound-categories') {
    if (!hasExactKeys(value, ['kind', 'limit'])) return null;
    if (
      !Number.isInteger(value.limit) ||
      (value.limit as number) < 1 ||
      (value.limit as number) > 6
    )
      return null;
    return { kind: 'bound-categories', limit: value.limit as number };
  }
  return null;
}

function parseBlock(
  value: unknown,
  path: string,
  layoutFamily: WebsiteLayoutFamily,
  surface: WebsitePageKind | 'chrome',
  issues: string[],
): WebsiteBlockManifest | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ['id', 'component', 'variant', 'slotIds', 'emptyBehavior'], ['dataSource'])
  ) {
    issues.push(`${path}:shape`);
    return null;
  }
  if (typeof value.id !== 'string' || !WEBSITE_BLOCK_ID_RE.test(value.id)) {
    issues.push(`${path}:id`);
    return null;
  }
  const blockPath = `${path}.${value.id}`;
  const component = value.component;
  if (
    typeof component !== 'string' ||
    !Object.prototype.hasOwnProperty.call(WEBSITE_COMPONENT_SURFACE, component)
  ) {
    issues.push(`${blockPath}:unknown_component`);
    return null;
  }
  const componentId = component as WebsiteComponentId;
  if (WEBSITE_COMPONENT_SURFACE[componentId] !== surface) {
    issues.push(`${blockPath}:component_surface`);
    return null;
  }
  if (
    typeof value.variant !== 'string' ||
    !WEBSITE_VARIANT_RE.test(value.variant) ||
    !isSupportedWebsiteVariant(layoutFamily, componentId, value.variant)
  ) {
    issues.push(`${blockPath}:unknown_variant`);
    return null;
  }
  if (!isOneOf(WEBSITE_EMPTY_BEHAVIORS, value.emptyBehavior)) {
    issues.push(`${blockPath}:empty_behavior`);
    return null;
  }
  if (
    !Array.isArray(value.slotIds) ||
    !value.slotIds.every((slotId): slotId is string => typeof slotId === 'string') ||
    new Set(value.slotIds).size !== value.slotIds.length
  ) {
    issues.push(`${blockPath}:slot_ids`);
    return null;
  }

  let dataSource: WebsiteBlockDataSource | undefined;
  if (value.dataSource !== undefined) {
    const parsed = parseDataSource(value.dataSource);
    if (!parsed) {
      issues.push(`${blockPath}:data_source`);
      return null;
    }
    dataSource = parsed;
  }

  const campaign = layoutFamily === 'editorial-fashion' && componentId === 'home.categoryCampaign';
  const boundTiles =
    (layoutFamily === 'editorial-fashion' || layoutFamily === 'luxury-jewelry') &&
    componentId === 'home.categoryTiles';
  if (
    (campaign && dataSource?.kind !== 'category-products') ||
    (boundTiles && dataSource?.kind !== 'bound-categories') ||
    (dataSource?.kind === 'category-products' && !campaign) ||
    (dataSource?.kind === 'bound-categories' && !boundTiles) ||
    (componentId === 'home.newsletter' && dataSource !== undefined)
  ) {
    issues.push(`${blockPath}:data_source`);
    return null;
  }
  return {
    id: value.id,
    component: componentId,
    variant: value.variant,
    slotIds: [...value.slotIds],
    emptyBehavior: value.emptyBehavior,
    ...(dataSource ? { dataSource } : {}),
  };
}

function parseDefaults(
  value: unknown,
  slot: Pick<WebsiteSlotSchema, 'type' | 'maxLength' | 'multiline' | 'linkTargets'>,
): WebsiteSlotDefault | null {
  if (!isRecord(value)) return null;
  if (value.kind === 'none') return hasExactKeys(value, ['kind']) ? { kind: 'none' } : null;

  if (value.kind === 'localized-text') {
    if (slot.type !== 'text' && slot.type !== 'rich-text') return null;
    if (!hasExactKeys(value, ['kind', 'ar', 'he', 'en'])) return null;
    for (const locale of ['ar', 'he', 'en'] as const) {
      const check = checkMerchantText(value[locale], {
        maxLength: slot.maxLength ?? 0,
        multiline: slot.type === 'rich-text' || slot.multiline === true,
      });
      if (!check.ok) return null;
    }
    return {
      kind: 'localized-text',
      ar: value.ar as string,
      he: value.he as string,
      en: value.en as string,
    };
  }
  if (value.kind === 'template-media') {
    if (slot.type !== 'media' || !hasExactKeys(value, ['kind', 'assetKey'])) return null;
    if (
      typeof value.assetKey !== 'string' ||
      value.assetKey.length > WEBSITE_TEMPLATE_ASSET_KEY_MAX_LENGTH ||
      !WEBSITE_TEMPLATE_ASSET_KEY_RE.test(value.assetKey)
    ) {
      return null;
    }
    return { kind: 'template-media', assetKey: value.assetKey };
  }
  if (value.kind === 'link') {
    if (slot.type !== 'link' || !hasExactKeys(value, ['kind', 'value'])) return null;
    const link = validateWebsiteLinkValue(value.value, slot.linkTargets ?? []);
    if (!link.ok) return null;
    // Templates are store-agnostic: a default can never name a store row.
    if (link.value.kind !== 'store-page' && link.value.kind !== 'contact') return null;
    return { kind: 'link', value: link.value };
  }
  if (value.kind === 'auto') {
    const isRef =
      slot.type === 'category-ref' ||
      slot.type === 'product-ref' ||
      slot.type === 'custom-collection-ref';
    return isRef && hasExactKeys(value, ['kind']) ? { kind: 'auto' } : null;
  }
  return null;
}

const SLOT_OPTIONAL_KEYS = [
  'ordinal',
  'maxLength',
  'multiline',
  'mediaKinds',
  'mediaAspect',
  'allowAutoplay',
  'decorative',
  'linkTargets',
  /** Epic #228 P5: media only, `{ linkSlotId }` of a link slot of the same item. */
  'clickable',
  /** Epic #228 P5: link only, `false` opts out of the platform `external` kind. */
  'externalLinks',
] as const;

function parseSlot(value: unknown, index: number, issues: string[]): WebsiteSlotSchema | null {
  const path = `slots[${index}]`;
  if (
    !isRecord(value) ||
    !hasExactKeys(
      value,
      ['id', 'type', 'semantic', 'required', 'requiresMerchantValue', 'defaults'],
      SLOT_OPTIONAL_KEYS,
    )
  ) {
    issues.push(`${path}:shape`);
    return null;
  }
  if (
    typeof value.id !== 'string' ||
    value.id.length > WEBSITE_SLOT_ID_MAX_LENGTH ||
    !WEBSITE_SLOT_ID_RE.test(value.id)
  ) {
    issues.push(`${path}:id`);
    return null;
  }
  const slotPath = `slot:${value.id}`;
  if (!isOneOf(WEBSITE_SLOT_TYPES, value.type)) {
    issues.push(`${slotPath}:unknown_slot_type`);
    return null;
  }
  if (!isOneOf(WEBSITE_SLOT_SEMANTICS, value.semantic)) {
    issues.push(`${slotPath}:semantic`);
    return null;
  }
  if (typeof value.required !== 'boolean' || typeof value.requiresMerchantValue !== 'boolean') {
    issues.push(`${slotPath}:flags`);
    return null;
  }

  const type = value.type;
  if ((value.id === 'chrome.newsletter.collection') !== (type === 'newsletter-collection')) {
    issues.push(`${slotPath}:newsletter_collection`);
    return null;
  }
  if (
    type === 'newsletter-collection' &&
    (value.id !== 'chrome.newsletter.collection' ||
      value.semantic !== 'newsletter.collection' ||
      value.required !== false ||
      value.requiresMerchantValue !== false ||
      !isRecord(value.defaults) ||
      !hasExactKeys(value.defaults, ['kind']) ||
      value.defaults.kind !== 'none' ||
      SLOT_OPTIONAL_KEYS.some((key) => value[key] !== undefined))
  ) {
    issues.push(`${slotPath}:newsletter_collection`);
    return null;
  }
  if (value.semantic === 'newsletter.collection' && type !== 'newsletter-collection') {
    issues.push(`${slotPath}:newsletter_collection`);
    return null;
  }
  const isText = type === 'text' || type === 'rich-text';
  const optional: {
    ordinal?: number;
    maxLength?: number;
    multiline?: boolean;
    mediaKinds?: WebsiteSlotSchema['mediaKinds'];
    mediaAspect?: WebsiteSlotSchema['mediaAspect'];
    allowAutoplay?: boolean;
    decorative?: boolean;
    clickable?: WebsiteSlotSchema['clickable'];
    linkTargets?: WebsiteSlotSchema['linkTargets'];
    externalLinks?: boolean;
  } = {};

  if (value.ordinal !== undefined) {
    if (
      !Number.isInteger(value.ordinal) ||
      (value.ordinal as number) < 1 ||
      (value.ordinal as number) > WEBSITE_MAX_SLOT_ORDINAL
    ) {
      issues.push(`${slotPath}:ordinal`);
      return null;
    }
    optional.ordinal = value.ordinal as number;
  }

  if (isText) {
    if (
      !Number.isInteger(value.maxLength) ||
      (value.maxLength as number) < 1 ||
      (value.maxLength as number) > WEBSITE_TEXT_MAX_LENGTH
    ) {
      issues.push(`${slotPath}:max_length`);
      return null;
    }
    optional.maxLength = value.maxLength as number;
  } else if (value.maxLength !== undefined) {
    issues.push(`${slotPath}:max_length`);
    return null;
  }

  if (value.multiline !== undefined) {
    if (type !== 'text' || typeof value.multiline !== 'boolean') {
      issues.push(`${slotPath}:multiline`);
      return null;
    }
    optional.multiline = value.multiline;
  }

  if (type === 'media') {
    if (
      !Array.isArray(value.mediaKinds) ||
      value.mediaKinds.length === 0 ||
      !value.mediaKinds.every((kind) => isOneOf(WEBSITE_MEDIA_KINDS, kind)) ||
      new Set(value.mediaKinds).size !== value.mediaKinds.length
    ) {
      issues.push(`${slotPath}:media_kinds`);
      return null;
    }
    optional.mediaKinds = [...value.mediaKinds] as WebsiteSlotSchema['mediaKinds'];
    if (value.mediaAspect !== undefined) {
      if (!isOneOf(WEBSITE_MEDIA_ASPECTS, value.mediaAspect)) {
        issues.push(`${slotPath}:media_aspect`);
        return null;
      }
      optional.mediaAspect = value.mediaAspect;
    }
    if (value.allowAutoplay !== undefined) {
      if (typeof value.allowAutoplay !== 'boolean' || !value.mediaKinds.includes('video')) {
        issues.push(`${slotPath}:allow_autoplay`);
        return null;
      }
      optional.allowAutoplay = value.allowAutoplay;
    }
    if (value.decorative !== undefined) {
      if (typeof value.decorative !== 'boolean') {
        issues.push(`${slotPath}:decorative`);
        return null;
      }
      optional.decorative = value.decorative;
    }
  } else if (
    value.mediaKinds !== undefined ||
    value.mediaAspect !== undefined ||
    value.allowAutoplay !== undefined ||
    value.decorative !== undefined
  ) {
    issues.push(`${slotPath}:media_fields`);
    return null;
  }

  // Epic #228 P5 (PD8): the link target itself is checked once every slot is
  // known (`clickableIssues`); here only the shape and the slot type.
  if (value.clickable !== undefined) {
    if (
      type !== 'media' ||
      !isRecord(value.clickable) ||
      !hasExactKeys(value.clickable, ['linkSlotId']) ||
      typeof value.clickable.linkSlotId !== 'string'
    ) {
      issues.push(`${slotPath}:clickable`);
      return null;
    }
    optional.clickable = { linkSlotId: value.clickable.linkSlotId };
  }

  if (type === 'link') {
    if (
      !Array.isArray(value.linkTargets) ||
      value.linkTargets.length === 0 ||
      !value.linkTargets.every((kind) => isOneOf(WEBSITE_LINK_TARGET_KINDS, kind)) ||
      new Set(value.linkTargets).size !== value.linkTargets.length
    ) {
      issues.push(`${slotPath}:link_targets`);
      return null;
    }
    optional.linkTargets = [...value.linkTargets] as WebsiteSlotSchema['linkTargets'];
  } else if (value.linkTargets !== undefined) {
    issues.push(`${slotPath}:link_targets`);
    return null;
  }

  // Epic #228 P5 (PD3): a manifest may opt a link slot out of `external`;
  // it never opts in (`external` is not a declarable `linkTargets` kind).
  if (value.externalLinks !== undefined) {
    if (type !== 'link' || typeof value.externalLinks !== 'boolean') {
      issues.push(`${slotPath}:external_links`);
      return null;
    }
    optional.externalLinks = value.externalLinks;
  }

  const defaults = parseDefaults(value.defaults, { type, ...optional });
  if (!defaults) {
    issues.push(`${slotPath}:defaults`);
    return null;
  }

  return {
    id: value.id,
    type,
    semantic: value.semantic,
    required: value.required,
    requiresMerchantValue: value.requiresMerchantValue,
    ...optional,
    defaults,
  };
}

function parseTheme(value: unknown, issues: string[]): WebsiteThemeSchema | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ['colorTokens', 'defaults', 'fontOptions', 'defaultFonts', 'swatches'])
  ) {
    issues.push('theme:shape');
    return null;
  }
  const isTokenList = (list: unknown, max: number): list is WebsiteThemeSchema['colorTokens'] =>
    Array.isArray(list) &&
    list.length <= max &&
    list.every((token) => isOneOf(WEBSITE_COLOR_TOKENS, token)) &&
    new Set(list).size === list.length;

  if (!isTokenList(value.colorTokens, WEBSITE_COLOR_TOKENS.length)) {
    issues.push('theme:color_tokens');
    return null;
  }
  if (!isTokenList(value.swatches, 4) || value.swatches.length === 0) {
    issues.push('theme:swatches');
    return null;
  }
  if (!isRecord(value.defaults) || !hasExactKeys(value.defaults, WEBSITE_COLOR_TOKENS)) {
    issues.push('theme:defaults');
    return null;
  }
  const defaults = {} as Record<(typeof WEBSITE_COLOR_TOKENS)[number], string>;
  for (const token of WEBSITE_COLOR_TOKENS) {
    const color = value.defaults[token];
    if (!isWebsiteHexColor(color) || color !== color.toLowerCase()) {
      issues.push(`theme:default_color:${token}`);
      return null;
    }
    defaults[token] = color;
  }
  if (findContrastFailures(defaults).length > 0) {
    issues.push('theme:contrast');
    return null;
  }

  const isFontList = (list: unknown): list is WebsiteThemeSchema['fontOptions']['heading'] =>
    Array.isArray(list) &&
    list.length > 0 &&
    list.every((font) => isOneOf(WEBSITE_FONT_IDS, font)) &&
    new Set(list).size === list.length;

  if (
    !isRecord(value.fontOptions) ||
    !hasExactKeys(value.fontOptions, ['heading', 'body']) ||
    !isFontList(value.fontOptions.heading) ||
    !isFontList(value.fontOptions.body)
  ) {
    issues.push('theme:font_options');
    return null;
  }
  const { heading, body } = value.fontOptions;
  if (
    !isRecord(value.defaultFonts) ||
    !hasExactKeys(value.defaultFonts, ['heading', 'body']) ||
    !isOneOf(heading, value.defaultFonts.heading) ||
    !isOneOf(body, value.defaultFonts.body)
  ) {
    issues.push('theme:default_fonts');
    return null;
  }

  return {
    colorTokens: [...value.colorTokens],
    defaults,
    fontOptions: { heading: [...heading], body: [...body] },
    defaultFonts: { heading: value.defaultFonts.heading, body: value.defaultFonts.body },
    swatches: [...value.swatches],
  };
}

/**
 * Epic #228 P5 (PD8): a media slot's `clickable.linkSlotId` must name a
 * `link` slot of the same manifest and the same item (`websiteSlotItemPath`).
 * Two media of one item (desktop and mobile) may share one link slot.
 */
function clickableIssues(slots: readonly WebsiteSlotSchema[]): string[] {
  const byId = new Map(slots.map((slot) => [slot.id, slot]));
  const issues: string[] = [];
  for (const slot of slots) {
    if (!slot.clickable) continue;
    const target = byId.get(slot.clickable.linkSlotId);
    if (
      !target ||
      target.type !== 'link' ||
      websiteSlotItemPath(target.id) !== websiteSlotItemPath(slot.id)
    ) {
      issues.push(`slot:${slot.id}:clickable`);
    }
  }
  return issues;
}

function expectedSlotPrefix(
  layoutFamily: WebsiteLayoutFamily,
  surface: WebsitePageKind | 'chrome',
  blockId: string,
): string {
  if (layoutFamily === 'legacy-engine-v2') return `${WEBSITE_LEGACY_SLOT_SCOPE}.`;
  const scope = surface === 'chrome' ? WEBSITE_CHROME_SLOT_SCOPE : slotScopeForPageKind(surface);
  return `${scope}.${blockId}.`;
}

/**
 * Full manifest validator used by catalog tooling and the editor. SQL runs a
 * defense-in-depth subset (capabilities, slot types, bounds) on insert.
 */
export function parseWebsiteTemplateManifest(json: unknown): WebsiteManifestParse {
  const issues: string[] = [];
  if (
    !isRecord(json) ||
    !hasExactKeys(json, [
      'schemaVersion',
      'rendererMajor',
      'layoutFamily',
      'chrome',
      'pages',
      'slots',
      'theme',
    ])
  ) {
    return { ok: false, issues: ['manifest:shape'] };
  }

  let serialized: string;
  try {
    serialized = JSON.stringify(json);
  } catch {
    return { ok: false, issues: ['manifest:shape'] };
  }
  if (utf8ByteLength(serialized) > WEBSITE_DOCUMENT_MAX_BYTES) {
    return { ok: false, issues: ['manifest:too_large'] };
  }

  if (json.schemaVersion !== 1) issues.push('manifest:schema_version');
  if (json.rendererMajor !== WEBSITE_RENDERER_MAJOR) issues.push('manifest:renderer_major');
  if (!isOneOf(WEBSITE_LAYOUT_FAMILIES, json.layoutFamily)) {
    return { ok: false, issues: [...issues, 'manifest:layout_family'] };
  }
  const layoutFamily = json.layoutFamily;

  if (!Array.isArray(json.slots) || json.slots.length > WEBSITE_MAX_SLOTS) {
    return { ok: false, issues: [...issues, 'slots:count'] };
  }
  const slots: WebsiteSlotSchema[] = [];
  const slotIds = new Set<string>();
  json.slots.forEach((raw, index) => {
    const slot = parseSlot(raw, index, issues);
    if (!slot) return;
    if (slotIds.has(slot.id)) {
      issues.push(`slot:${slot.id}:duplicate`);
      return;
    }
    slotIds.add(slot.id);
    slots.push(slot);
  });

  const owners = new Map<string, string>();
  const parseBlocks = (
    raw: unknown,
    surface: WebsitePageKind | 'chrome',
    max: number,
  ): WebsiteBlockManifest[] => {
    if (!Array.isArray(raw) || raw.length > max) {
      issues.push(`${surface}:blocks`);
      return [];
    }
    const blocks: WebsiteBlockManifest[] = [];
    const blockIds = new Set<string>();
    for (const rawBlock of raw) {
      const block = parseBlock(rawBlock, surface, layoutFamily, surface, issues);
      if (!block) continue;
      if (blockIds.has(block.id)) {
        issues.push(`${surface}.${block.id}:duplicate`);
        continue;
      }
      blockIds.add(block.id);
      const prefix = expectedSlotPrefix(layoutFamily, surface, block.id);
      for (const slotId of block.slotIds) {
        if (!slotIds.has(slotId)) issues.push(`${surface}.${block.id}:unknown_slot:${slotId}`);
        else if (!slotId.startsWith(prefix)) {
          issues.push(`${surface}.${block.id}:slot_scope:${slotId}`);
        } else if (owners.has(slotId)) issues.push(`slot:${slotId}:multiple_owners`);
        else owners.set(slotId, `${surface}.${block.id}`);
      }
      blocks.push(block);
    }
    const ordinals = blocks.flatMap((block) =>
      block.dataSource?.kind === 'category-products' ? [block.dataSource.categoryOrdinal] : [],
    );
    if (new Set(ordinals).size !== ordinals.length)
      issues.push(`${surface}:duplicate_category_ordinal`);
    if (blocks.filter((block) => block.component === 'home.newsletter').length > 1)
      issues.push(`${surface}:duplicate_newsletter`);
    return blocks;
  };

  const chrome = parseBlocks(json.chrome, 'chrome', WEBSITE_MAX_CHROME_BLOCKS);
  const newsletters = chrome.filter((block) => block.component === 'chrome.newsletter');
  if (newsletters.length > 1 || newsletters.some((block) => block.id !== 'newsletter')) {
    issues.push('chrome:newsletter_singleton');
  }
  for (const slot of slots) {
    if (
      slot.type === 'newsletter-collection' &&
      !newsletters.some((block) => block.slotIds.includes(slot.id))
    ) {
      issues.push(`slot:${slot.id}:newsletter_owner`);
    }
  }
  if (layoutFamily !== 'legacy-engine-v2') {
    for (const component of WEBSITE_REQUIRED_CHROME_COMPONENTS) {
      if (!chrome.some((block) => block.component === component)) {
        issues.push(`chrome:missing:${component}`);
      }
    }
  }

  const pages = {} as Record<WebsitePageKind, { blocks: WebsiteBlockManifest[] }>;
  // Without all seven pages nothing below is meaningful: stop here rather
  // than report every slot of the missing page as unowned.
  if (!isRecord(json.pages) || !hasExactKeys(json.pages, WEBSITE_PAGE_KINDS)) {
    return { ok: false, issues: [...issues, 'pages:shape'] };
  }
  for (const pageKind of WEBSITE_PAGE_KINDS) {
    const page = json.pages[pageKind];
    if (!isRecord(page) || !hasExactKeys(page, ['blocks'])) {
      issues.push(`${pageKind}:shape`);
      continue;
    }
    const blocks = parseBlocks(page.blocks, pageKind, WEBSITE_MAX_BLOCKS_PER_PAGE);
    if (blocks.length === 0) issues.push(`${pageKind}:empty`);
    const required = WEBSITE_REQUIRED_PAGE_COMPONENT[pageKind];
    if (required && blocks.filter((block) => block.component === required).length !== 1) {
      issues.push(`${pageKind}:missing:${required}`);
    }
    pages[pageKind] = { blocks };
  }

  for (const slot of slots) {
    if (!owners.has(slot.id)) issues.push(`slot:${slot.id}:unowned`);
  }
  issues.push(...clickableIssues(slots));

  const theme = parseTheme(json.theme, issues);

  if (issues.length > 0 || !theme) return { ok: false, issues };
  return {
    ok: true,
    manifest: {
      schemaVersion: 1,
      rendererMajor: WEBSITE_RENDERER_MAJOR,
      layoutFamily,
      chrome,
      pages,
      slots,
      theme,
    },
  };
}

export interface WebsiteSlotOwner {
  readonly surface: WebsitePageKind | 'chrome';
  readonly block: WebsiteBlockManifest;
}

/** slotId → owning surface/block, derived from a parsed manifest. */
export function indexWebsiteSlotOwners(
  manifest: WebsiteTemplateManifest,
): ReadonlyMap<string, WebsiteSlotOwner> {
  const owners = new Map<string, WebsiteSlotOwner>();
  for (const block of manifest.chrome) {
    for (const slotId of block.slotIds) owners.set(slotId, { surface: 'chrome', block });
  }
  for (const pageKind of WEBSITE_PAGE_KINDS) {
    for (const block of manifest.pages[pageKind].blocks) {
      for (const slotId of block.slotIds) owners.set(slotId, { surface: pageKind, block });
    }
  }
  return owners;
}
